/**
 * @file AdminMeetingsPage.tsx
 * @brief Super Admin → platform-wide MeetingIntel list.
 *
 * @details
 * The cross-company MeetingIntel table: every boardroom meeting on the
 * platform, with Owner + Company columns the company-scope list doesn't have.
 * Metadata only (B2/W4, DES 22-10-2026): owner, company, status, minutes,
 * charge, date; no engagement score or other content. Reuses the shared
 * cells (`MeetingStatusBadge`, `MeetingRowActions` in `admin` scope, which
 * offers Retry / Regenerate / Reprocess / Delete and no export). Row click opens
 * `/admin/meetings/:id`. URL-synced filters (search / company / owner / dates).
 *
 * @dependencies
 * - @tanstack/react-table (ColumnDef)
 * - components/data/DataTable, components/ui/Avatar
 * - features/admin-meetings/{api, schemas, components/AdminMeetingsFilters}
 * - features/company-meetings/components/* (shared cells)
 *
 * @references
 * - features/company-meetings/CompanyMeetingsPage.tsx (company-scope sibling)
 * - features/sessions/SessionsPage.tsx (platform-wide table pattern)
 *
 * @author DES
 * @date 08-07-2026
 * @updated 29-07-2026 — GridBlock end-to-end
 */

import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2 } from "lucide-react";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { Avatar } from "@/components/ui/Avatar";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import {
  formatDateTime,
  formatDurationSeconds,
  formatFullName,
  formatMoneyMinor,
  getInitials,
} from "@/shared/utils/formatters";
import { StaffNoContentNote } from "@/components/feedback/StaffNoContent";
import { companyGroupMeta } from "@/shared/utils/tableGroups";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { MeetingStatusBadge } from "@/features/company-meetings/components/MeetingStatusBadge";
import { MeetingRowActions } from "@/features/company-meetings/components/MeetingRowActions";
import {
  adminMeetingsPagination,
  meetingCompanyGroupKey,
  meetingCompanyOwnerGroupKey,
  useAdminMeetingsGroupRows,
  useAdminMeetingsGroupSummary,
  useAdminMeetingsList,
} from "./api";
import {
  formatMeetingMinutes,
  meetingOwnerLabel,
  type AdminMeetingRow,
  type AdminMeetingsListParams,
} from "./schemas";
import {
  AdminMeetingsFilters,
  AdminMeetingsHeaderFilters,
} from "./components/AdminMeetingsFilters";

// ----------------------------------------------------------------------
// URL <-> params serialization
// ----------------------------------------------------------------------
function paramsFromSearch(search: URLSearchParams): AdminMeetingsListParams {
  return {
    page: Number(search.get("page")) || 1,
    pageSize: Number(search.get("pageSize")) || 20,
    search: search.get("search") || undefined,
    from: search.get("from") || undefined,
    to: search.get("to") || undefined,
    owner_id: search.get("owner_id") || undefined,
    company_id: search.get("company_id") || undefined,
  };
}

function searchFromParams(p: AdminMeetingsListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.pageSize && p.pageSize !== 20) usp.set("pageSize", String(p.pageSize));
  if (p.search) usp.set("search", p.search);
  if (p.from) usp.set("from", p.from);
  if (p.to) usp.set("to", p.to);
  if (p.owner_id) usp.set("owner_id", p.owner_id);
  if (p.company_id) usp.set("company_id", p.company_id);
  return usp;
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function AdminMeetingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);

  const isGrouped = !params.company_id && !params.owner_id;
  /**
   * Grouped (no company or user picked): every company and user with a
   * meeting, from the server (`group=company_owner`), instead of fetching
   * every page and grouping on the client (DES 01-10-2026: "haan superadmin
   * pages pe bhi yehi kar do"). A user group fetches its own meetings when
   * opened, 25 at a time. No total cards here, so the list is off when grouped.
   */
  const list = useAdminMeetingsList(params, { enabled: !isGrouped });
  const summary = useAdminMeetingsGroupSummary(params, isGrouped);
  // Open groups (company and user keys) live in the URL (`open=key:batches,…`).
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, so matches are visible.
  const searching = isGrouped && Boolean(params.search?.trim());
  // Only user (leaf) groups fetch rows.
  const openList = useMemo(() => {
    const keys = searching
      ? (summary.data ?? []).map((g) => meetingCompanyOwnerGroupKey(g.company_id, g.owner_id))
      : [...openGroups.keys()].filter((k) => k.includes("/"));
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, summary.data, openGroups]);
  const groupRows = useAdminMeetingsGroupRows(params, openList);
  const groups = useMemo<TableRowGroup<AdminMeetingRow>[] | undefined>(() => {
    if (!isGrouped || !summary.data?.length) return undefined;
    const companies = new Map<string, TableRowGroup<AdminMeetingRow>>();
    for (const g of summary.data) {
      const companyKey = meetingCompanyGroupKey(g.company_id);
      let company = companies.get(companyKey);
      if (!company) {
        company = {
          key: companyKey,
          ...companyGroupMeta(g.company_name),
          rows: [],
          children: [],
          total: 0,
        };
        companies.set(companyKey, company);
      }
      const key = meetingCompanyOwnerGroupKey(g.company_id, g.owner_id);
      const idx = openList.findIndex((o) => o.key === key);
      const q = idx >= 0 ? groupRows[idx] : undefined;
      const name = formatFullName(g.first_name, g.last_name);
      company.children!.push({
        key,
        label: name !== "—" ? name : g.email ?? "Unknown",
        avatarSrc: g.avatar_url,
        avatarInitials: getInitials(g.first_name, g.last_name, g.email),
        rows: q?.data?.meetings ?? [],
        total: g.count,
        loading: Boolean(q?.isFetching),
        error: Boolean(q?.isError),
        onRetry: () => void q?.refetch(),
      });
      company.total = (company.total ?? 0) + g.count;
    }
    const tree = [...companies.values()];
    for (const c of tree) c.children!.sort((a, b) => a.label.localeCompare(b.label));
    return tree.sort((a, b) => a.label.localeCompare(b.label));
  }, [isGrouped, summary.data, openList, groupRows]);
  const rows = isGrouped
    ? (groups ?? []).flatMap((c) => (c.children ?? []).flatMap((u) => u.rows))
    : list.data?.meetings ?? [];
  const refetchAll = () => (isGrouped ? summary.refetch() : list.refetch());

  const paginationMeta = adminMeetingsPagination(list.data, params);
  const pagination: PaginationState | undefined =
    !isGrouped && list.data
      ? {
          page: paginationMeta.page,
          limit: paginationMeta.limit,
          total: paginationMeta.total,
          totalPages: paginationMeta.totalPages,
        }
      : undefined;

  const setParams = (next: AdminMeetingsListParams) =>
    setSearchParams(searchFromParams(next), { replace: false });

  const openDetail = (id: string) => navigate(`/admin/meetings/${id}`);

  const columns = useMemo<ColumnDef<AdminMeetingRow>[]>(
    () => [
      {
        id: "meeting",
        header: "Meeting",
        cell: ({ row }) => {
          const m = row.original;
          if (isGrouped) {
            return (
              <p className="truncate text-sm font-medium">
                {m.title?.trim() || "Untitled meeting"}
              </p>
            );
          }
          return (
            <div className="flex items-center gap-3">
              <Avatar
                initials={getInitials(
                  m.owner_first_name,
                  m.owner_last_name,
                  m.owner_email,
                )}
                alt=""
                size="md"
                tone="brand"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {m.title?.trim() || "Untitled meeting"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {meetingOwnerLabel(m)}
                </p>
              </div>
            </div>
          );
        },
      },
      ...(isGrouped
        ? []
        : [
            {
              id: "company",
              header: "Company",
              cell: ({ row }) => {
                const name = row.original.company_name?.trim();
                return name ? (
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {name}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">Personal</span>
                );
              },
            } satisfies ColumnDef<AdminMeetingRow>,
          ]),
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <MeetingStatusBadge
            status={row.original.status}
            failureReason={row.original.failure_reason}
          />
        ),
      },
      {
        id: "duration",
        header: "Duration",
        cell: ({ row }) => (
          <div className="text-sm tabular-nums">
            {formatDurationSeconds(
              Number(row.original.total_duration_seconds ?? 0),
            )}
            <p className="text-xs text-muted-foreground">
              {formatMeetingMinutes(row.original)}
            </p>
          </div>
        ),
      },
      {
        // `total_cost_cents` on a meeting = what the customer was billed.
        id: "billed",
        header: "Billed",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatMoneyMinor(row.original.total_cost_cents, row.original.currency ?? "GBP")}
          </span>
        ),
      },
      {
        id: "started",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(row.original.started_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <MeetingRowActions
              meeting={row.original}
              scope="admin"
              onView={openDetail}
              onDeleted={() => void refetchAll()}
            />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isGrouped],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Meeting Intel</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Every boardroom meeting across the platform: owner, company,
              status, minutes and cost. Click a row for its details, retry or
              delete.
            </p>
            <StaffNoContentNote className="mt-2" />
          </div>
          {/* The filters, top right (DES 01-10-2026): text pills, then the
              date circle. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <AdminMeetingsHeaderFilters value={params} onChange={setParams} />
            <DateRangePicker
              variant="circle"
              from={params.from}
              to={params.to}
              onChange={({ from, to }) => setParams({ ...params, from, to, page: 1 })}
            />
          </div>
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          <DataTable<AdminMeetingRow, unknown>
            variant="cell"
            columns={columns}
            data={rows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            groupNestedStyle={isGrouped ? "timeline" : "plain"}
            groupItemLabel="meetings"
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            isLoading={isGrouped ? summary.isLoading : list.isLoading}
            isFetching={isGrouped ? summary.isFetching : list.isFetching}
            error={isGrouped ? summary.error : list.error}
            onRetry={() => void refetchAll()}
            rowKey={(m) => m.id}
            pagination={pagination}
            onPageChange={(page) => setParams({ ...params, page })}
            onLimitChange={(limit) =>
              setParams({ ...params, pageSize: limit, page: 1 })
            }
            onRowClick={(m) => openDetail(m.id)}
            emptyShape="meeting"
            emptyTitle="No meetings match those filters"
            emptyDescription="Adjust the filters or wait for fresh recordings from the platform."
          />
        </GridCell>
      </GridBlock>

      {/* The search, docked at the bottom like Notes (DES 01-10-2026). */}
      <PageDock>
        <AdminMeetingsFilters value={params} onChange={setParams} />
      </PageDock>
    </div>
  );
}
